import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';

export default function ForgotPassword(): JSX.Element {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setSubmitted(true);
    } catch (err: unknown) {
      // Backend returns 200 even on failure to avoid enumeration; only network / 5xx errors land here.
      setError(getApiErrorMessage(err, 'We could not process your request. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
    >
      <div className="w-full max-w-md">
        <div
          className="rounded-2xl p-9"
          style={{ background: '#FFFFFF', boxShadow: '0 32px 80px rgba(0,0,0,0.45)' }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Forgot your password?</h2>
          <p className="text-gray-400 text-sm mb-7">
            Enter the email associated with your account and we will send you a link to create a new one.
          </p>

          {submitted ? (
            <div
              className="p-4 rounded-lg text-sm"
              style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}
            >
              <p className="font-medium mb-1">Check your inbox</p>
              <p className="text-xs">
                If <span className="font-mono">{email}</span> is registered, you will receive
                a link to reset your password in the next few minutes.
                The link expires in 15 minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className="p-3 rounded-lg text-sm"
                  style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}
                >
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="forgot-email" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all"
                  style={{
                    background: '#F8F9FC',
                    border: '1.5px solid #E8ECF4',
                    color: '#111827',
                  }}
                  placeholder="name@company.com"
                  required
                  maxLength={254}
                  disabled={loading}
                  onFocus={(e) => (e.currentTarget.style.borderColor = GOLD)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = '#E8ECF4')}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-sm transition-all"
                style={{
                  background: loading ? '#E5E7EB' : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
                  color: loading ? '#9CA3AF' : NAVY,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Sending...' : 'Send link'}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-xs text-gray-400">
            <Link to="/login" className="font-medium" style={{ color: GOLD }}>
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
